import { useEffect, useRef, useState } from 'react';

const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js';

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
    }
  ) => string;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

/**
 * Load the Cloudflare Turnstile script once and render a widget into `ref`,
 * capturing its token. The effect is keyed on `open` so the widget renders when
 * the dialog opens (and the container is in the DOM) and is torn down — with the
 * token cleared — when it closes, so reopening yields a fresh challenge. When
 * `siteKey` is undefined the hook is inert and the token stays `null` (caller
 * should omit the field from the payload).
 */
export function useTurnstile(
  siteKey: string | undefined,
  ref: React.RefObject<HTMLDivElement>,
  open: boolean
): string | null {
  const [token, setToken] = useState<string | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!siteKey || !open) return;

    let cancelled = false;

    const renderWidget = () => {
      if (cancelled || !ref.current || !window.turnstile) return;
      if (widgetIdRef.current) return;
      try {
        widgetIdRef.current = window.turnstile.render(ref.current, {
          sitekey: siteKey,
          callback: (nextToken: string) => setToken(nextToken),
          'expired-callback': () => setToken(null),
          'error-callback': () => setToken(null),
        });
      } catch (error) {
        console.error('Turnstile render failed:', error);
      }
    };

    let scriptEl: HTMLScriptElement | null = null;

    if (window.turnstile) {
      renderWidget();
    } else {
      scriptEl = document.querySelector<HTMLScriptElement>(
        `script[src="${TURNSTILE_SCRIPT_SRC}"]`
      );
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.src = TURNSTILE_SCRIPT_SRC;
        scriptEl.async = true;
        scriptEl.defer = true;
        document.head.appendChild(scriptEl);
      }
      scriptEl.addEventListener('load', renderWidget);
    }

    return () => {
      cancelled = true;
      if (scriptEl) {
        scriptEl.removeEventListener('load', renderWidget);
      }
      const widgetId = widgetIdRef.current;
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch (error) {
          console.error('Turnstile remove failed:', error);
        }
      }
      widgetIdRef.current = null;
      setToken(null);
    };
  }, [siteKey, ref, open]);

  return token;
}
